/**
 * Feedback's words (ADR-081's amendment of 2026-10-05, items 3 to 5): the menu's entry and its form, the page's own
 * sentences for a report that got no usable answer, and the sentences the gateway's feedback route and the report's
 * checks answer with. "Feedback" is the author's word for the entry; "Report a bug", the sentence saying what is
 * sent, the saved line and the sentences of a paused or removed account are the amendment's drafts for the author,
 * as is everything else here.
 *
 * `refused` holds the route's sentences, which it sends as plain text and the form shows as they come. `report`
 * holds what the report's checks answer, which the game's route, the build server and the development server all
 * send: they describe a request no form of ours makes, so they are written for whoever built the request.
 * `{reference}` is the first characters of a stored report's id; `{time}` and `{when}` are moments on the reader's
 * own clock; `{bytes}` and `{limit}` are whole numbers of bytes, printed as digits alone; `{kinds}` is the kinds a
 * report may be, joined by a comma and a space; `{client}` is a client's tag and `{prefix}` the opening of a PNG
 * data URL.
 */
export const FEEDBACK = {
  item: "Feedback",
  titles: { player: "Report a bug", staff: "Feedback" },
  kind: "Kind",
  kinds: { bug: "Bug report", feature: "Feature request" },
  title: "Title",
  description: "Description",
  sent: "Your report is sent to the OdysseyTales team with your account's family name, your browser's name and the game version.",
  withLog: "Include the game's log",
  withScreenshot: "Include a screenshot",
  screenshotNote: "A screenshot shows other players' names and chat.",
  cancel: "Cancel",
  close: "Close",
  send: "Send",
  sending: "Sending",
  saved: "Thank you. Your report is saved. Reference: {reference}",
  off: "Feedback is turned off for this account. To ask for it back, contact the team through odysseytales.net.",
  pausedToday: "Feedback is paused. You can send feedback again at {time}.",
  pausedLater: "Feedback is paused. You can send feedback again on {when}.",
  notSent: "The report was not sent. Check your connection and try again.",
  notAccepted: "The report was not accepted. Try again in a moment.",
  refused: {
    rate: "Too many requests; try again in a minute.",
    session: "Log in again to send feedback.",
    account: "Too many reports from this account; try again in a few minutes.",
    off: "Feedback is turned off for this account. To ask for it back, contact the team through odysseytales.net.",
    paused: "Feedback is paused for this account.",
    age: "A new account can send its first report after 10 minutes. Try again shortly.",
    playerAccount: "You have sent several reports just now. Try again in a few minutes.",
    daily: "You have reached today's limit of reports. Try again tomorrow.",
    playerAddress: "Too many reports from this network just now. Try again in an hour.",
    ceiling: "The game has had many reports today. Try again tomorrow.",
    kind: "This account can send bug reports only.",
    duplicate: "You already sent this report.",
    unchecked: "The server could not check your account; try again.",
    unstored: "The report could not be stored; try again.",
    tooLarge: "The report is too large. Send it without the screenshot.",
    untitled: "A title is required.",
    method: "Use POST.",
    contentType: "Send the report as application/json.",
    broken: "The report did not arrive whole; try again.",
    untaken: "The report could not be taken; try again.",
    client: "client must be {client}",
  },
  report: {
    size: "report is {bytes} bytes, over the {limit} byte limit; send it without the screenshot",
    body: "body must be a JSON report",
    kind: "kind must be one of {kinds}",
    title: "title is required",
    client: "client must be a short lower case tag such as game or editor",
    screenshotUrl: "screenshot must be a {prefix}... data URL",
    screenshotPng: "screenshot must hold a PNG image",
  },
} as const;
